import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChoiceList } from './GameStatePanels'
import { useVip } from './VipContext'
import { choose, choiceDeath, recommendedChoice, type GameState } from '../game'
import type { Choice } from '../content-adapter'
import { playGameSound } from '../animation/gameAudio'

const roasts = ['铁废物', '又选错？', '999 没白花吧', '韭菜之王就这水平', '孙割看了都摇头', '这题孙割三岁就会']
export function VipChoices({ state, choices, onChange }: { state: GameState; choices: Choice[]; onChange: (next: GameState) => void }) {
  const vip = useVip()
  const [warning, setWarning] = useState<{ index: number; text: string } | null>(null)
  const [correction, setCorrection] = useState<{ index: number; roast: string; phase: 'scold' | 'rewind' | 'fixed' } | null>(null)
  const busy = useRef(false)
  const mountedState = useRef(state)
  mountedState.current = state
  const timers = useRef<number[]>([])
  const finish = useRef(onChange)
  finish.current = onChange
  const sound = useRef(vip.soundEnabled)
  sound.current = vip.soundEnabled
  const scoldFocus = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (correction && correction.phase !== 'fixed') scoldFocus.current?.focus()
  }, [correction])
  useEffect(() => {
    if (!vip.soundEnabled) window.speechSynthesis?.cancel()
  }, [vip.soundEnabled])
  useEffect(() => () => {
    timers.current.forEach(window.clearTimeout)
    window.speechSynthesis?.cancel()
    busy.current = false
  }, [state])
  const pick = (index: number) => {
    if (busy.current) return
    const best = vip.svipStatus === 'active' ? recommendedChoice(state) : null
    if (best !== null && best !== index) {
      busy.current = true
      const roast = roasts[Math.floor(Math.random() * roasts.length)]
      setCorrection({ index: best, roast, phase: 'scold' })
      if (sound.current) {
        playGameSound('scold')
        try {
          if (window.speechSynthesis && window.SpeechSynthesisUtterance) {
            const utterance = new SpeechSynthesisUtterance(`${roast}！`)
            utterance.lang = 'zh-CN'; utterance.rate = 1.05; utterance.pitch = .8
            window.speechSynthesis.cancel(); window.speechSynthesis.speak(utterance)
          }
        } catch { /* Text and sound effect remain available without speech support. */ }
      }
      const later = (delay: number, action: () => void) => timers.current.push(window.setTimeout(() => { if (mountedState.current === state) action() }, delay))
      later(vip.reduceMotion ? 200 : 1500, () => { setCorrection({ index: best, roast, phase: 'rewind' }); if (sound.current) playGameSound('rewind') })
      later(vip.reduceMotion ? 400 : 2400, () => setCorrection({ index: best, roast, phase: 'fixed' }))
      later(vip.reduceMotion ? 500 : 2800, () => { window.speechSynthesis?.cancel(); finish.current(choose(state, best)) })
      return
    }
    const death = vip.vipStatus === 'active' && !state.branchId ? choiceDeath(state, index) : null
    if (death) { setWarning({ index, text: death.text }); return }
    busy.current = true
    onChange(choose(state, index))
  }
  return <>
    {warning ? <div className="vip-confirm" role="alert"><strong>☠ 死路预警</strong><p>{warning.text}</p><p>这一步可能结束本次人生，确定继续吗？</p><div className="vip-confirm-actions"><button autoFocus className="secondary-button" onClick={() => setWarning(null)}>重新选择</button><button className="primary-button" onClick={() => { if (busy.current) return; busy.current = true; onChange(choose(state, warning.index)) }}>仍然选择</button></div></div> : <ChoiceList state={state} choices={choices} onChoose={pick} disabled={Boolean(correction)} fixedIndex={correction?.phase === 'fixed' ? correction.index : undefined} />}
    {correction && correction.phase !== 'fixed' && createPortal(<div id="scold-overlay" className={`show boom${vip.reduceMotion ? ' reduced-motion' : ''}${correction.phase === 'rewind' ? ' rewind' : ''}`} role="dialog" aria-modal="true" aria-label="宇宙至尊自动改命" tabIndex={-1} ref={scoldFocus} onKeyDown={event => { if (event.key === 'Tab') event.preventDefault() }}><div className="scold-stamp">{correction.roast}！</div><div className="scold-sub">系统已接管本次人生抉择</div><div className="scold-rw">⏪ 时光回溯中 · 正在替你修正…</div></div>, document.body)}
  </>
}
